import React from "react";
import { useTheme } from "@mui/material";
import { Dialog } from "@/Components/v2/atoms/Dialog";
import { DialogContent } from "@/Components/v2/atoms/Dialog/DialogContent";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Box } from "@/Components/v2/atoms/Box";
import { Typography } from "@/Components/v2/atoms/Typography";
import { ActionPlanStatus } from "@mutiny-group/sbo-openapi-client";
import StatusLabel from "./StatusLabel";
import { IconButton } from "@/Components/v2/atoms/Button";
import CloseIcon from "@/Components/Icons/CloseIcon";

interface StatusModalProps {
  open: boolean;
  onClose: () => void;
}

const statusExplanations = [
  {
    status: ActionPlanStatus.OnTrack,
    description:
      "Your revenue trajectory is at or above the revenue goal you have set, meaning our model predicts you will hit or exceed target.",
  },
  {
    status: ActionPlanStatus.AtRisk,
    description:
      "Your revenue goal is within the confidence interval of your revenue trajectory. You may miss your goal.",
  },
  {
    status: ActionPlanStatus.OffTrack,
    description:
      "Your revenue goal is outside the confidence interval of your revenue trajectory. Our model predicts you will miss your goal.",
  },
];

export const StatusModal: React.FC<StatusModalProps> = ({ open, onClose }) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "40px",
        },
      }}
    >
      <DialogContent>
        <Stack width="100%" alignItems="flex-end">
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Stack spacing={3} p={2} pt={0}>
          <Stack spacing={1.5}>
            <Typography variant="h6" fontWeight={600} fontSize={20}>
              Action Plan Status
            </Typography>
            <Typography variant="body2" fontSize={14}>
              The Status of your Action Plan shows the probability that you will achieve your
              revenue goal with the current spending patterns.
            </Typography>
          </Stack>

          <Stack spacing={0.5}>
            {statusExplanations.map(({ status, description }) => (
              <Box
                key={status}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "20px",
                  p: "12px",
                  backgroundColor: theme.palette.mode === "light" ? "#F6F9FD" : "#2B3033",
                }}
              >
                <Box sx={{ minWidth: "95px", display: "flex" }}>
                  <StatusLabel status={status} />
                </Box>
                <Typography variant="body2" fontSize={14}>
                  {description}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default StatusModal;
